/**
 * AuthLayout — branded split-screen wrapper for the pre-auth pages
 * (design card 3a). Left: deep brand-maroon panel selling the product in
 * Nickson. Right: the form column (Clerk's <SignIn>/<SignUp> or the legacy
 * JWT form). Kept presentational so both auth paths can reuse it.
 */
import type { ReactNode } from 'react';
import { useSiteConfig } from '../../utils/siteConfig';
import { IS_OPERATOR_HOST, PRODUCT_COMPANY_LINE } from '../../utils/productBrand';
import './AuthLayout.css';

export default function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title?: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  // On the operator domain the root provider serves the RunMyTrivia product
  // brand. On a venue domain it serves that venue org's brand, so a venue
  // site never shows the product brand.
  const { get } = useSiteConfig();
  const brandName = get('brandName');
  const brandLogoUrl = get('brandLogoUrl');
  // Deep brand color drives the left panel only (not the Radix accent) so
  // the panel keeps the tenant's true brand even where the accent is a
  // nearest-Radix approximation.
  const brandColor = get('brandColor', '#840032');

  return (
    <div className="auth-split">
      <aside className="auth-brand" style={{ background: brandColor }}>
        {/* Wordmark: the product brand on the operator domain, the venue
            org brand on a venue domain. */}
        {brandLogoUrl ? (
          <img className="auth-wordmark" src={brandLogoUrl} alt={brandName} style={{ maxHeight: 40 }} />
        ) : (
          <span className="auth-wordmark">{brandName}</span>
        )}
        <div className="auth-brand-stack">
          <span className="auth-headline">Run the whole trivia night from one place.</span>
          <span className="auth-subcopy">
            Write quizzes, launch game nights, drive the projector, and score
            sheets — hosts sign in here. Players never need an account; they
            just use the PIN on screen.
          </span>
        </div>
        <span className="auth-circle auth-circle-1" />
        <span className="auth-circle auth-circle-2" />
      </aside>

      <main className="auth-form-col">
        <div className="auth-form">
          {(title || subtitle) && (
            <div className="auth-form-head">
              {title && <div className="auth-form-title">{title}</div>}
              {subtitle && <div className="auth-form-subtitle">{subtitle}</div>}
            </div>
          )}
          {children}
          {footer && <div className="auth-form-footer">{footer}</div>}
        </div>
        {/* The company line shows with the product brand only. A venue
            domain shows the venue brand, so it does not show this line. */}
        {IS_OPERATOR_HOST && <div className="auth-company">{PRODUCT_COMPANY_LINE}</div>}
      </main>
    </div>
  );
}
